import { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, ShieldCheck } from "lucide-react";
import { PLATFORM_NAME, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PasskeyUnlock } from "@/components/PasskeyUnlock";
import { InstallPrompt } from "@/components/InstallPrompt";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import AdminConsole from "@/pages/AdminConsole";

export default function AdminGate() {
  const { user, loading, login, logout } = useAuth();
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink-900">
        <Loader2 className="h-6 w-6 animate-spin text-gold" />
      </div>
    );
  }

  if (user && user.role === "platform_admin") return <AdminConsole />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const data = await login({ email: form.email, password: form.password });
      if (data.user?.role !== "platform_admin") {
        await logout();
        setError("That account is not the platform owner. Operators sign in at /office.");
      }
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="admin-gate" className="flex min-h-screen items-center justify-center bg-ink-900 p-6">
      <form onSubmit={submit} data-testid="admin-login-form"
        className="w-full max-w-md space-y-5 rounded-2xl border border-ink-600 bg-ink-800/70 p-8">
        <img src="/limolinkx-logo.png" alt={PLATFORM_NAME}
          className="mx-auto h-20 w-20 rounded-lg bg-white/95 object-contain p-1" />
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-slate-500">{PLATFORM_NAME}</p>
        <div className="flex items-center gap-2 text-sky-300">
          <ShieldCheck className="h-4 w-4" />
          <p className="font-mono text-[11px] uppercase tracking-widest">Platform owner console</p>
        </div>
        <div>
          <label className="mb-2 block font-mono text-[10px] uppercase tracking-widest text-slate-500">Email</label>
          <Input data-testid="admin-email" type="email" required value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="h-11 border-ink-600 bg-ink-900 text-slate-100 focus-visible:ring-gold" />
        </div>
        <div>
          <label className="mb-2 block font-mono text-[10px] uppercase tracking-widest text-slate-500">Password</label>
          <Input data-testid="admin-password" type="password" required value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            className="h-11 border-ink-600 bg-ink-900 text-slate-100 focus-visible:ring-gold" />
        </div>
        {error && <p data-testid="admin-login-error" className="text-sm text-red-400">{error}</p>}
        <Button data-testid="admin-login-submit" type="submit" disabled={busy}
          className="h-11 w-full bg-gold text-ink-900 hover:bg-gold-light">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
        </Button>
        <PasskeyUnlock scope="admin" email={form.email} testId="admin-passkey"
          onSignedIn={async (data) => {
            if (data.user?.role !== "platform_admin") {
              await logout();
              setError("That account is not the platform owner. Operators sign in at /office.");
            }
          }} />
        <p className="text-xs text-slate-500">
          This console shows every subscriber, your revenue and account controls. Operators cannot reach it.
        </p>
        <InstallPrompt testId="admin-install"
          blurb="Install the owner console on this phone and the icon opens straight here — not the passenger or chauffeur app." />
        <p className="text-center text-xs text-slate-500">
          <Link to="/forgot-password" data-testid="admin-forgot-link" className="text-gold">
            Forgot your password?
          </Link>
        </p>
      </form>
    </div>
  );
}
